// Schönecke: Abholort-/Abholtag-Auswahl im Warenkorb.
// Optionen kommen serverseitig aus Liquid. Dieses Skript:
//  - befüllt die Abholtage beim Standwechsel,
//  - speichert die Auswahl per /cart/update.js als Cart Attributes,
//  - blendet Tage nach dem Bestellschluss aus,
//  - sperrt den Checkout ohne gültige Auswahl.
// Horizon morpht den Warenkorb (Kinder werden ersetzt) – daher Event-Delegation am Element
// und erneuter Abgleich per MutationObserver.

const TAG_FMT = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
const SCHLUSS_FMT = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function tagLabel(stand, iso) {
  const zeit = stand?.zt?.[iso];
  return TAG_FMT.format(parseIso(iso)) + (zeit ? ` · ${zeit} Uhr` : '');
}

function parseIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Letzte Auswahl des Kunden – Quelle der Wahrheit, falls Horizon den Warenkorb mit veraltetem
// Server-HTML neu rendert (morph setzt sonst Auswahl/disabled auf den Serverstand zurück).
const auswahl = { stand: null, tag: null };
let speicherKette = Promise.resolve();
let letzteAttribute = null;
let offeneSpeicherungen = 0;

// Abholort-Adresse strukturiert für den WinWeb-Import (Lieferadresse im Beleg).
// Metaobjekt-Feld "ort": Zeile 1 = Name, Zeile 2 = Straße, letzte Zeile = "PLZ Ort".
function lieferadresse(stand) {
  const leer = { lieferadresse_name: '', lieferadresse_strasse: '', lieferadresse_plz: '', lieferadresse_ort: '', lieferadresse_land: '' };
  if (!stand) return leer;
  const zeilen = (stand.ort || '').split('\n').map((z) => z.trim()).filter(Boolean);
  const letzte = zeilen.length > 1 ? zeilen[zeilen.length - 1] : '';
  const m = letzte.match(/^(\d{5})\s+(.+)$/);
  return {
    lieferadresse_name: zeilen[0] || stand.name,
    lieferadresse_strasse: zeilen.length > 2 ? zeilen.slice(1, -1).join(', ') : '',
    lieferadresse_plz: m ? m[1] : '',
    lieferadresse_ort: m ? m[2] : letzte,
    lieferadresse_land: 'DE',
  };
}

class AbholungPicker extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.addEventListener('change', this.#onChange);
    this._observer = new MutationObserver(() => this.abgleichen());
    this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    if (auswahl.stand === null && this.standSelect?.value) {
      auswahl.stand = this.standSelect.value;
      auswahl.tag = this.tagSelect?.value || '';
    }
    this.abgleichen();
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._init = false;
    this.removeEventListener('change', this.#onChange);
  }

  get schluss() {
    return new Date(this.dataset.bestellschluss || '2026-12-19T23:59');
  }

  get staende() {
    try {
      return JSON.parse(this.querySelector('[data-staende]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  get standSelect() {
    return this.querySelector('[data-stand]');
  }

  get tagSelect() {
    return this.querySelector('[data-tag]');
  }

  get stand() {
    return this.staende.find((s) => s.id === this.standSelect?.value);
  }

  offen() {
    return new Date() < this.schluss;
  }

  offeneTage(stand) {
    if (!stand || !this.offen()) return [];
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    return stand.tage.filter((t) => parseIso(t) >= heute).sort();
  }

  // Nach jedem (Neu-)Rendern: Bestellschluss-Hinweis, abgelaufene Tage, Validierung
  abgleichen() {
    if (this._abgleich) return;
    this._abgleich = true;
    try {
      const hinweis = this.querySelector('[data-schluss]');
      const text = this.offen()
        ? `Bestellschluss: ${SCHLUSS_FMT.format(this.schluss)} Uhr`
        : 'Der Bestellschluss ist erreicht – Vorbestellungen sind leider nicht mehr möglich.';
      if (hinweis && hinweis.textContent !== text) hinweis.textContent = text;
      if (!this.offen()) {
        if (this.standSelect) this.standSelect.disabled = true;
        if (this.tagSelect) this.tagSelect.disabled = true;
      }
      // Auswahl wiederherstellen, falls ein Neu-Rendern sie zurückgesetzt hat
      if (auswahl.stand !== null && this.standSelect && this.standSelect.value !== auswahl.stand) {
        this.standSelect.value = auswahl.stand;
      }
      const stand = this.stand;
      const offen = this.offeneTage(stand);
      const sel = this.tagSelect;
      if (sel && this.offen()) {
        const vorhanden = [...sel.options].map((o) => o.value).filter(Boolean);
        const passt = stand && !sel.disabled && offen.every((t) => vorhanden.includes(t)) && vorhanden.every((t) => offen.includes(t));
        if (stand && !passt) this.fuelleTage();
        if (!stand && !sel.disabled) this.fuelleTage();
        if (auswahl.tag && sel.value !== auswahl.tag && offen.includes(auswahl.tag)) sel.value = auswahl.tag;
      }
      this.pruefen();
    } finally {
      this._abgleich = false;
    }
  }

  fuelleTage() {
    const sel = this.tagSelect;
    const stand = this.stand;
    const warAktiv = this._abgleich;
    this._abgleich = true;
    sel.length = 0;
    if (!stand) {
      sel.add(new Option('Erst Abholort wählen', ''));
      sel.disabled = true;
    } else {
      sel.add(new Option('Bitte Abholtag wählen', ''));
      for (const t of this.offeneTage(stand)) sel.add(new Option(tagLabel(stand, t), t));
      sel.disabled = false;
    }
    const zeiten = this.querySelector('[data-zeiten]');
    if (zeiten) {
      zeiten.hidden = !stand?.zeiten;
      zeiten.textContent = stand?.zeiten ? `Abholzeiten: ${stand.zeiten}` : '';
    }
    this._abgleich = warAktiv;
  }

  pruefen() {
    const stand = this.stand;
    const tag = this.tagSelect?.value;
    const ok = Boolean(stand && tag && this.offeneTage(stand).includes(tag));
    if (ok) {
      this.classList.remove('abholung-picker--fehler');
      const fehler = this.querySelector('[data-fehler]');
      if (fehler) fehler.hidden = true;
    }
    return ok;
  }

  zeigeFehler() {
    this.classList.add('abholung-picker--fehler');
    const fehler = this.querySelector('[data-fehler]');
    if (fehler) fehler.hidden = false;
    (this.standSelect?.value ? this.tagSelect : this.standSelect)?.focus();
  }

  #onChange = (event) => {
    if (event.target === this.standSelect) {
      auswahl.stand = this.standSelect.value;
      auswahl.tag = '';
      this.fuelleTage();
    }
    if (event.target === this.tagSelect) auswahl.tag = this.tagSelect.value;
    if (event.target === this.standSelect || event.target === this.tagSelect) this.speichern();
  };

  async speichern() {
    const ok = this.pruefen();
    const stand = this.stand;
    const tag = this.tagSelect.value;
    const attributes = ok
      ? {
          Abholort: stand.name,
          Abholort_Adresse: (stand.ort || '').replace(/\s*\n\s*/g, ', '),
          Abholtag: tagLabel(stand, tag),
          abholtag_iso: tag,
          marktstand_id: stand.id,
          winweb_kundennummer: stand.nr,
          ...lieferadresse(stand),
        }
      : { Abholort: '', Abholort_Adresse: '', Marktstand: '', Abholtag: '', abholtag_iso: '', marktstand_id: stand?.id || '', winweb_kundennummer: '', ...lieferadresse(null) };

    // Weitere Picker (Drawer + Warenkorbseite) synchron halten
    for (const p of document.querySelectorAll('abholung-picker')) {
      if (p === this || !p.standSelect) continue;
      p.standSelect.value = this.standSelect.value;
      p.fuelleTage();
      p.tagSelect.value = tag;
      p.pruefen();
    }

    // Speicherungen nacheinander ausführen, damit eine ältere (z. B. "nur Abholort") nie eine neuere
    // (Abholort + Tag) überschreibt; zusätzlich wird immer nur der jeweils letzte Stand gesendet.
    letzteAttribute = attributes;
    offeneSpeicherungen += 1;
    speicherKette = speicherKette
      .then(async () => {
        if (!letzteAttribute) return;
        const senden = letzteAttribute;
        letzteAttribute = null;
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: senden }),
        });
      })
      .catch((e) => console.warn('[abholung-picker]', e))
      .finally(() => {
        offeneSpeicherungen -= 1;
      });
    window.__schoeneckeAbholungSpeichern = speicherKette;
    return speicherKette;
  }
}

if (!customElements.get('abholung-picker')) {
  customElements.define('abholung-picker', AbholungPicker);

  // Checkout sperren ohne gültige Auswahl – greift auch nach Neu-Rendern des Warenkorbs.
  const sperre = (event) => {
    const button = event.target.closest?.('[name="checkout"], a[href*="/checkout"]');
    const istSubmit = event.type === 'submit' && event.submitter?.name === 'checkout';
    if (!button && !istSubmit) return;
    // Schönecke: ohne gültige Anzahlung (wird gerade ergänzt) kein Checkout
    if (window.__schoeneckeAnzahlungOk === false) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.schoeneckeAnzahlungAbgleichen?.();
      return;
    }
    const picker =
      [...document.querySelectorAll('abholung-picker')].find((p) => p.offsetParent !== null) ||
      document.querySelector('abholung-picker');
    if (!picker) return;
    if (picker.pruefen()) {
      // Läuft noch eine Speicherung, Checkout kurz zurückhalten und danach erneut auslösen
      if (offeneSpeicherungen > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const ziel = button || event.submitter;
        speicherKette.then(() => ziel?.click());
      }
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    picker.zeigeFehler();
  };
  document.addEventListener('click', sperre, true);
  document.addEventListener('submit', sperre, true);
}
